/**
 * Shared color palette and name-to-color logic used for avatars, chart progress, etc.
 * Deterministic: same name always maps to the same color.
 */

export const AVATAR_COLOR_PALETTE: readonly string[] = [
  'C2537B',
  'A83E88',
  '906CBE',
  '5B639A',
  '4078D2',
  '3DB3B3',
  '49B687',
  'A7BD44',
  'CD9037',
  'D9B530',
  'E86157',
  'E86157',
];

/** Palette for metric/chart progress (cycles, day charts). Hex values without #. */
export const METRIC_COLOR_PALETTE: readonly string[] = [
  '00cbff',
  '967adc',
  'ff6b3e',
  '2196f3',
  '5acd52',
  'f44336',
  '9c27b0',
  'ff9800',
];

function getCharValue(char: string): number {
  const lowerChar = char.toLowerCase();

  if (lowerChar >= 'a' && lowerChar <= 'z') {
    return lowerChar.charCodeAt(0) - 'a'.charCodeAt(0) + 1; // a=1, b=2, etc.
  }

  if (char >= '0' && char <= '9') {
    return parseInt(char, 10);
  }

  return 1; // anything not a letter or number gets 1
}

function calculateAvatarColorIndex(name: string): number {
  if (!name) return 0;

  const sum = name.split('').reduce((total, char) => total + getCharValue(char), 0);

  return (sum - 1) % AVATAR_COLOR_PALETTE.length;
}

function calculateMetricColorIndex(name: string): number {
  if (!name) return 0;
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return hash % METRIC_COLOR_PALETTE.length;
}

/**
 * Returns a hex color (e.g. "#3DB3B3") deterministically from a name string.
 * Use for avatars, chart progress strokes, or any UI that should have a stable color per name.
 * @param name - Identifier (e.g. user name for avatar, metricId/chart name for metrics).
 * @param type - 'avatar' uses AVATAR_COLOR_PALETTE; 'metric' uses METRIC_COLOR_PALETTE.
 */
export function getColorForName(name: string, type: 'avatar' | 'metric' = 'avatar'): string {
  const normalized = (name ?? '').split(' ').join('');
  const index = type === 'metric' ? calculateMetricColorIndex(normalized) : calculateAvatarColorIndex(normalized);
  const palette = type === 'metric' ? METRIC_COLOR_PALETTE : AVATAR_COLOR_PALETTE;
  return `#${palette[index]}`;
}
